import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

export const PROGRAMS = [
    { to: "/programs/pieces-of-hope", label: "Pieces of Hope", sub: "An Autism Awareness Program" },
    { to: "/programs/purpose-in-the-process", label: "Purpose in the Process", sub: "Finding meaning in your season" },
    { to: "/programs/haven-of-hope", label: "Haven of Hope", sub: "Shelter, safety & restoration" },
    { to: "/programs/gok-second-chance", label: "GOK Second Chance Program", sub: "Re-entry & redemption" },
];

export const Header = () => {
    const [open, setOpen] = useState(false);
    const [progOpen, setProgOpen] = useState(false); // desktop hover state
    const [mobileProgOpen, setMobileProgOpen] = useState(false); // mobile expandable
    const navItems = [
        { to: "/episodes", label: "Episodes" },
        { to: "/guests", label: "Guests" },
        { to: "/programs", label: "Programs", dropdown: true },
        { to: "/team", label: "Meet the Team" },
        { to: "/shop", label: "Shop" },
        { to: "/about", label: "About" },
        { to: "/apply", label: "Be a Guest" },
        { to: "/meeting", label: "Join Meeting" },
        { to: "/donate", label: "Donate", highlight: true },
    ];

    return (
        <header
            data-testid="site-header"
            className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-black/10"
        >
            <div className="px-6 md:px-12 lg:px-20 py-3 md:py-4 flex items-center justify-between">
                <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
                    <img
                        src="https://customer-assets.emergentagent.com/job_podcast-merch-hub/artifacts/9rqltbjo_Transparentgoklogo.png"
                        alt="The Gift of Kindness Podcast"
                        className="h-16 md:h-20 w-auto select-none transition-transform duration-500 group-hover:scale-[1.03]"
                        draggable="false"
                    />
                    <span className="sr-only">The Gift of Kindness Podcast</span>
                </Link>

                <nav className="hidden md:flex items-center gap-8 lg:gap-9">
                    {navItems.map((n) => {
                        if (n.dropdown) {
                            return (
                                <div
                                    key={n.to}
                                    className="relative"
                                    onMouseEnter={() => setProgOpen(true)}
                                    onMouseLeave={() => setProgOpen(false)}
                                >
                                    <button
                                        data-testid="nav-programs"
                                        onClick={() => setProgOpen((v) => !v)}
                                        className="eyebrow text-[12px] tracking-[0.2em] transition-colors duration-300 text-black hover:text-[#B8860B] flex items-center gap-1"
                                        aria-haspopup="true"
                                        aria-expanded={progOpen}
                                    >
                                        {n.label}
                                        <ChevronDown size={12} strokeWidth={2} className={`transition-transform duration-300 ${progOpen ? "rotate-180" : ""}`} />
                                    </button>
                                    <div
                                        data-testid="programs-dropdown"
                                        className={`absolute left-1/2 top-full pt-4 -translate-x-1/2 w-[340px] transition-all duration-300 ${
                                            progOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2 pointer-events-none"
                                        }`}
                                    >
                                        <div className="bg-white border border-black/10 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.15)]">
                                            <div className="eyebrow text-[#B8860B] text-[10px] px-6 pt-5 pb-3 border-b border-black/5">
                                                Our Programs
                                            </div>
                                            {PROGRAMS.map((p) => (
                                                <Link
                                                    key={p.to}
                                                    to={p.to}
                                                    onClick={() => setProgOpen(false)}
                                                    data-testid={`nav-program-${p.to.split("/").pop()}`}
                                                    className="group block px-6 py-4 border-b border-black/5 last:border-b-0 hover:bg-[#FAFAFA] transition-colors"
                                                >
                                                    <div className="font-serif text-lg tracking-tight text-black group-hover:text-[#B8860B] transition-colors leading-tight">
                                                        {p.label}
                                                    </div>
                                                    <div className="text-xs text-black/50 mt-1 font-light">{p.sub}</div>
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            );
                        }
                        return (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                                className={({ isActive }) =>
                                    n.highlight
                                        ? `eyebrow text-[12px] tracking-[0.2em] px-4 py-2 border transition-colors duration-300 ${
                                              isActive
                                                  ? "bg-[#D4AF37] border-[#D4AF37] text-black"
                                                  : "border-[#D4AF37] text-[#B8860B] hover:bg-[#D4AF37] hover:text-black"
                                          }`
                                        : `eyebrow text-[12px] tracking-[0.2em] transition-colors duration-300 ${
                                              isActive ? "text-[#B8860B]" : "text-black hover:text-[#B8860B]"
                                          }`
                                }
                            >
                                {n.label}
                            </NavLink>
                        );
                    })}
                </nav>

                <div className="flex items-center gap-4">
                    <button
                        data-testid="mobile-menu-toggle"
                        className="md:hidden p-2"
                        onClick={() => setOpen(!open)}
                        aria-label="Menu"
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>
            {open && (
                <div className="md:hidden border-t border-black/10 bg-white px-6 py-4 flex flex-col gap-1">
                    {navItems.map((n) => {
                        if (n.dropdown) {
                            return (
                                <div key={n.to} className="border-b border-black/5">
                                    <button
                                        onClick={() => setMobileProgOpen((v) => !v)}
                                        data-testid="mobile-nav-programs"
                                        className="eyebrow text-[12px] py-3 w-full flex items-center justify-between"
                                    >
                                        {n.label}
                                        <ChevronDown size={14} className={`transition-transform ${mobileProgOpen ? "rotate-180" : ""}`} />
                                    </button>
                                    {mobileProgOpen && (
                                        <div className="pb-3 pl-4 flex flex-col gap-1">
                                            {PROGRAMS.map((p) => (
                                                <NavLink
                                                    key={p.to}
                                                    to={p.to}
                                                    onClick={() => { setOpen(false); setMobileProgOpen(false); }}
                                                    data-testid={`mobile-nav-program-${p.to.split("/").pop()}`}
                                                    className="py-2 font-serif text-base text-black/80 hover:text-[#B8860B]"
                                                >
                                                    {p.label}
                                                </NavLink>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        }
                        return (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                                className={`eyebrow text-[12px] py-3 border-b border-black/5 ${n.highlight ? "text-[#B8860B]" : ""}`}
                            >
                                {n.label}
                            </NavLink>
                        );
                    })}
                </div>
            )}
        </header>
    );
};
